// The Suppliers tab of a draft with RFP invitations on (Lane 1440, Ryan's mockup v1): the
// RFP invitation letter (Wingman's draft until the buyer saves it), then one line per
// supplier: contact, email, letter sent, confirmed. A row opens that supplier's own copy
// of the letter to copy or download. Every filled letter is the server's (one fill).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { saveBlob } from './DocumentsTab';
import { AddSuppliersPicker } from './WorkspaceLists';

// The saved fields each mark writes (the server's _MARK_SQL in
// helpers/sourcing_invitations.py): confirming also sets "sent" and clears "declined";
// declining clears "confirmed".
const MARK_FIELDS = {
  'sent:true': ['sent'], 'sent:false': ['sent'],
  'confirmed:true': ['confirmed', 'declined', 'sent'], 'confirmed:false': ['confirmed'],
  'declined:true': ['declined', 'confirmed'], 'declined:false': ['declined'],
};

// The save-queue keys of one mark, per supplier: a failure leaves its own field failed;
// a success clears every field it writes (so a good "Confirmed" also clears a failed
// "Mark sent" or "Declined" of that supplier, and nothing else).
export function markKeys(eventId, ids, field, value) {
  const writes = MARK_FIELDS[`${field}:${Boolean(value)}`] || [field];
  const key = (id, f) => `mark:${eventId}:${id}:${f}`;
  return { fail: ids.map((id) => key(id, field)), ok: ids.flatMap((id) => writes.map((f) => key(id, f))) };
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
      <path d="M3 8.5l3.2 3.2L13 4.8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// The letter as the buyer reads it; in the shared copy the two placeholders are marked.
function LetterText({ subject, body, marked }) {
  const mark = (text) => {
    if (!marked) return text;
    return text.split(/(\{contact\}|\{supplier\})/).map((part, i) => (
      part === '{contact}' || part === '{supplier}'
        ? <span key={i} className="srce-letter-fill">{part === '{contact}' ? 'contact name' : 'supplier name'}</span>
        : part));
  };
  return (
    <div className="srce-letter">
      <div className="srce-letter-subj">Subject: {mark(subject)}</div>
      {body.split(/\n\n/).map((p, i) => (
        <p key={i}>{p.split('\n').map((line, j, all) => (
          <React.Fragment key={j}>{mark(line)}{j < all.length - 1 && <br />}</React.Fragment>))}</p>
      ))}
    </div>
  );
}

function LetterCard({ api, eventId, letter, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [base, setBase] = useState(0);
  // The facts the editor opened with: saved as the letter's basis, so a change made while
  // the buyer types is still named afterwards (Codex code r1 #4).
  const [facts, setFacts] = useState(null);
  const start = (from) => {
    setSubject(from.subject); setBody(from.body); setBase(letter.letter_revision); setFacts(letter.facts);
    setError(''); setEditing(true);
  };
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await api.saveInvitationLetter(eventId, { subject, body, base_revision: base, basis: facts });
      setEditing(false);
      await onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const download = () => api.downloadInvitationLetters(eventId)
    .then((b) => saveBlob(b, 'RFP invitations.docx')).catch((e) => setError(e.message));
  return (
    <section className="srce-card2" data-testid="srce-letter">
      <div className="srce-card2-h">
        <span className="srce-card2-t">RFP invitation letter</span>
        <span className="srce-mut srce-ell srce-letter-hint">One letter for every supplier. Each copy fills in its own name and contact.</span>
        <span className="srce-push srce-row">
          {letter.editable && !editing && <button type="button" className="srce-btn srce-btn--sm" onClick={() => start(letter)}>Edit</button>}
          <button type="button" className="srce-btn srce-btn--sm" onClick={download}>Download all (Word)</button>
        </span>
      </div>
      <div className="srce-card2-f srce-inv-col">
        {letter.names_supplier.length > 0 && (
          <div className="srce-warnbox" role="status">This letter names one of your suppliers ({letter.names_supplier.join(', ')}), and every supplier gets it. Change the title or the letter.</div>
        )}
        {letter.stale.length > 0 && !editing && (
          <div className="srce-warnbox" role="status">
            The RFP changed since this letter was saved: {letter.stale.join(', ')}. Check the letter.
            {letter.editable && <button type="button" className="ll-clr" onClick={() => start(letter.draft)}>Use Wingman's updated draft</button>}
          </div>
        )}
        {error && <div className="srce-error" role="alert">{error}</div>}
        {editing ? (
          <div className="srce-inv-col srce-letter-edit">
            <label className="srce-label" htmlFor="srce-letter-subject">Subject</label>
            <input id="srce-letter-subject" className="srce-input srce-letter-subj-in" value={subject} maxLength={300} disabled={busy}
              onChange={(e) => setSubject(e.target.value)} />
            <label className="srce-label" htmlFor="srce-letter-body">Letter</label>
            <textarea id="srce-letter-body" className="srce-input srce-letter-ta" value={body} maxLength={20000} disabled={busy}
              onChange={(e) => setBody(e.target.value)} />
            <span className="srce-row">
              <button type="button" className="srce-btn srce-btn--primary" disabled={busy || !subject.trim() || !body.trim()} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
              <button type="button" className="ll-clr" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
              {letter.edited && <button type="button" className="ll-clr" disabled={busy} onClick={() => start(letter.draft)}>Use Wingman's draft</button>}
              <span className="srce-mut">Keep {'{contact}'} and {'{supplier}'}: each copy fills them in. Dates you type stay as typed.</span>
            </span>
          </div>
        ) : <LetterText subject={letter.subject} body={letter.body} marked />}
      </div>
    </section>
  );
}

function ContactCell({ supplier, field, onSave, placeholder }) {
  const value = field === 'email' ? supplier.email : (supplier.contact_name || '');
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value);
  useEffect(() => { if (!editing) setText(value); }, [value, editing]);
  if (editing) {
    return (
      <input className="srce-input srce-inv-in" type={field === 'email' ? 'email' : 'text'} value={text} autoFocus maxLength={255}
        aria-label={`${field === 'email' ? 'Email' : 'Contact'} for ${supplier.supplier_name}`} placeholder={placeholder}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => { setEditing(false); if (text.trim() !== value) onSave(text.trim()); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
          if (e.key === 'Escape') { setText(value); setEditing(false); }
        }} />
    );
  }
  let shown;
  if (field === 'email' && !supplier.email) shown = <span className="srce-pill srce-pill--warn">Email needed</span>;
  else if (field === 'email' && !supplier.email_ok) shown = <span title={supplier.email}><span className="srce-pill srce-pill--warn">Check email</span> {supplier.email}</span>;
  else if (!value) shown = <span className="srce-mut">Add contact</span>;
  else shown = value;
  return (
    <button type="button" className="srce-inv-cellbtn srce-ell" title={value || undefined}
      aria-label={`${value ? 'Change' : 'Add'} the ${field === 'email' ? 'email' : 'contact'} for ${supplier.supplier_name}`}
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}>{shown}</button>
  );
}

function SupplierLetter({ api, eventId, supplier }) {
  const [state, setState] = useState('');
  const text = useRef(null);
  const whole = `Subject: ${supplier.letter.subject}\n\n${supplier.letter.body}`;
  const copy = async () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('no clipboard');
      await navigator.clipboard.writeText(whole);
      setState('Letter copied. Paste it into your email.');
    } catch (e) {
      if (text.current) { text.current.focus(); text.current.select(); }
      setState('Press Ctrl+C or Cmd+C to copy.');
    }
  };
  const download = () => api.downloadInvitationLetters(eventId, supplier.supplier_id)
    .then((b) => saveBlob(b, `RFP invitation - ${supplier.supplier_name}.docx`))
    .catch((e) => setState(e.message));
  return (
    <div className="srce-inv-open" role="region" aria-label={`Letter for ${supplier.supplier_name}`}>
      {supplier.names_supplier.length > 0 && (
        <div className="srce-warnbox" role="status">This copy names another of your suppliers ({supplier.names_supplier.join(', ')}).</div>)}
      <LetterText subject={supplier.letter.subject} body={supplier.letter.body} />
      {/* The plain text, for the copy fallback (selected when the clipboard is blocked). */}
      <textarea ref={text} className="srce-sr" readOnly value={whole} tabIndex={-1} aria-hidden="true" />
      <span className="srce-row">
        <button type="button" className="srce-btn srce-btn--sm" onClick={copy}>Copy letter</button>
        <button type="button" className="srce-btn srce-btn--sm" onClick={download}>Download (Word)</button>
        {state && <span className="srce-mut" role="status">{state}</span>}
      </span>
    </div>
  );
}

export default function InvitationLetterTab({ api, eventId, refreshKey, saveQueue, onChanged, onOpenQualifications }) {
  const [view, setView] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  // Each supplier's contact as last typed: a second edit (the name right after the email)
  // builds on it, never on a row the screen has not re-read yet (Codex code r1 #1). Kept
  // with the workspace's save queue, so it survives leaving this tab (Codex code r2 #2).
  const { typed, saving, savedAt } = saveQueue.memory.current;
  useEffect(() => { setView(null); setOpen(null); }, [eventId]);
  useEffect(() => {
    let live = true;
    const startedAt = Date.now();
    api.invitationLetter(eventId).then((v) => {
      if (!live) return;
      // A contact takes the saved values again only from a read that began after its last
      // save finished (an older read may not show it yet; Codex code r3 #1).
      Object.keys(typed).forEach((sid) => {
        if (!saving[sid] && (savedAt[sid] || 0) < startedAt) delete typed[sid];
      });
      setView(v);
      setError('');
    })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
    // typed, saving and savedAt are the queue's own objects (one ref, never replaced), so
    // listing them never re-runs the read.
  }, [api, eventId, refreshKey, tick, typed, saving, savedAt]);
  const reload = useCallback(async () => { setTick((n) => n + 1); if (onChanged) await onChanged(); }, [onChanged]);

  // Marks go through the same queue as the contacts: Launch waits for a "Declined" still
  // being saved and stops on one that failed (Codex code r2 #1).
  const mark = (ids, field, value) => {
    setBusy(true);
    setError('');
    // Failures are kept per supplier and per saved field (Codex code r3 #2); a good mark
    // clears exactly the fields it saves for those suppliers (Codex code r4).
    return saveQueue.run(markKeys(eventId, ids, field, value), async () => {
      try {
        await api.markInvitation(eventId, { supplier_ids: ids, field, value });
      } catch (e) {
        setError(e.message);
        throw e;
      } finally {
        setBusy(false);
      }
      await reload();
    });
  };
  // A contact cell: saved with the other field as it is (the publish draft's one home).
  const saveContact = (s, field, value) => {
    const sid = `${eventId}:${s.supplier_id}`;
    const prior = typed[sid] || { email: s.email || '', name: s.contact_name || '' };
    const next = { ...prior, [field]: value };
    typed[sid] = next;
    saving[sid] = (saving[sid] || 0) + 1;
    saveQueue.run(`contact:${sid}`, async () => {
      try {
        await api.savePublishDraft(eventId, { contacts: [{ supplier_id: s.supplier_id, email: next.email, name: next.name }] });
        savedAt[sid] = Date.now();
      } catch (e) {
        setError(e.message);
        throw e;
      } finally {
        saving[sid] -= 1;
      }
      await reload();
    });
  };

  if (error && !view) return <div className="srce-error" role="alert">{error}</div>;
  if (!view) return <div className="ll-loading">Loading…</div>;
  const invitees = view.suppliers.filter((s) => !s.declined_at);
  const sent = invitees.filter((s) => s.letter_sent_at).length;
  const confirmed = invitees.filter((s) => s.confirmed_at).length;
  const unsent = invitees.filter((s) => !s.letter_sent_at).map((s) => s.supplier_id);
  return (
    <div className="srce-inv-col srce-inv">
      <LetterCard api={api} eventId={eventId} letter={view} onSaved={reload} />
      <section className="srce-card2" data-testid="srce-invitation-table">
        <div className="srce-card2-h">
          <span className="srce-card2-t">Suppliers</span>
          <span className="srce-mut srce-ell">{`${view.suppliers.length} suppliers, ${sent} of ${invitees.length} letters sent, ${confirmed} confirmed`}</span>
          <span className="srce-push srce-row">
            <button type="button" className="srce-btn srce-btn--sm" disabled={busy || unsent.length === 0}
              onClick={() => mark(unsent, 'sent', true)}>Mark all sent</button>
            <button type="button" className="srce-btn srce-btn--sm" onClick={onOpenQualifications}>Qualifications</button>
            <button type="button" className="srce-btn srce-btn--primary srce-btn--sm" onClick={() => setAdding(true)}>Add suppliers</button>
          </span>
        </div>
        {error && <div className="srce-error srce-card2-f" role="alert">{error}</div>}
        {view.suppliers.length === 0 ? <div className="srce-mut srce-card2-f">No suppliers yet.</div> : (
          <div className="srce-inv-tablewrap">
            <table className="srce-inv-table">
              <thead><tr><th>Supplier</th><th>Contact</th><th>Email</th><th>RFP invitation</th><th>Confirmed</th></tr></thead>
              <tbody>
                {view.suppliers.map((s) => {
                  const isOpen = open === s.supplier_id;
                  const declined = Boolean(s.declined_at);
                  return (
                    <React.Fragment key={s.supplier_id}>
                      <tr className={`srce-inv-row${isOpen ? ' srce-inv-row--open' : ''}${declined ? ' srce-inv-row--declined' : ''}`}
                        onClick={() => setOpen(isOpen ? null : s.supplier_id)}>
                        <td>
                          <button type="button" className="srce-disclose srce-ell" aria-expanded={isOpen}
                            onClick={(e) => { e.stopPropagation(); setOpen(isOpen ? null : s.supplier_id); }}>
                            <span className="srce-disclose-i" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>{s.supplier_name}
                          </button>
                        </td>
                        <td><ContactCell supplier={s} field="name" placeholder="Name" onSave={(v) => saveContact(s, 'name', v)} /></td>
                        <td>{declined ? <span className="srce-mut">Not invited</span>
                          : <ContactCell supplier={s} field="email" placeholder="Email" onSave={(v) => saveContact(s, 'email', v)} />}</td>
                        <td>
                          {declined ? <span className="srce-mut">-</span> : (
                            <button type="button" disabled={busy}
                              className={`srce-inv-mark${s.letter_sent_at ? ' srce-inv-mark--yes' : ''}`}
                              aria-pressed={Boolean(s.letter_sent_at)}
                              aria-label={s.letter_sent_at ? `Letter sent to ${s.supplier_name}. Click to mark not sent.` : `Mark the letter sent to ${s.supplier_name}`}
                              onClick={(e) => { e.stopPropagation(); mark([s.supplier_id], 'sent', !s.letter_sent_at); }}>
                              {s.letter_sent_at ? <><CheckIcon /> Sent</> : 'Mark sent'}
                            </button>)}
                        </td>
                        <td>
                          <select className={`srce-inv-select${s.confirmed_at ? ' srce-inv-select--yes' : declined ? ' srce-inv-select--no' : ''}`}
                            disabled={busy} aria-label={`Did ${s.supplier_name} confirm?`}
                            value={s.confirmed_at ? 'confirmed' : declined ? 'declined' : ''}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === 'confirmed') mark([s.supplier_id], 'confirmed', true);
                              else if (v === 'declined') mark([s.supplier_id], 'declined', true);
                              else if (s.confirmed_at) mark([s.supplier_id], 'confirmed', false);
                              else if (declined) mark([s.supplier_id], 'declined', false);
                            }}>
                            <option value="">Mark confirmed</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="declined">Declined</option>
                          </select>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="srce-inv-letterrow"><td colSpan={5}>
                          <SupplierLetter api={api} eventId={eventId} supplier={s} />
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="srce-mut srce-card2-f">Declined suppliers are not invited when you launch. Suppliers who have not confirmed still get the Wingman email.</div>
      </section>
      {adding && <AddSuppliersPicker api={api} eventId={eventId} onChanged={reload} onClose={() => setAdding(false)} />}
    </div>
  );
}
